import React, { useState } from 'react';
import SourcePopover from 'src/components/ui/SourcePopover';
import { getFigureSasLink } from 'src/features/chat/api';
import { SourceElementInfo } from '../api';

interface ActionSourceTooltipProps {
  /** The source element to display */
  sourceElement: SourceElementInfo;
  /** Document external ID for loading figure URLs */
  documentExternalId?: string | null;
  /** The trigger element */
  children: React.ReactNode;
  /** Whether clicking opens the wiki */
  clickable?: boolean;
}

/**
 * Tooltip for action extraction source elements.
 * Handles loading figures via SAS URLs, uses shared SourcePopover for display.
 */
export default function ActionSourceTooltip({
  sourceElement,
  documentExternalId,
  children,
  clickable = false
}: ActionSourceTooltipProps) {
  const [figureUrl, setFigureUrl] = useState<string | null>(null);
  const [figureLoading, setFigureLoading] = useState(false);

  // Load figure URL when needed
  const loadFigure = () => {
    if (
      sourceElement.type === 'figure' &&
      sourceElement.figure_id &&
      documentExternalId &&
      !figureUrl &&
      !figureLoading
    ) {
      setFigureLoading(true);
      getFigureSasLink(documentExternalId, sourceElement.figure_id)
        .then((res) => setFigureUrl(res.url))
        .catch((err) => console.error('Failed to load figure:', err))
        .finally(() => setFigureLoading(false));
    }
  };

  // Build the display content
  const getDisplayContent = (): string => {
    if (sourceElement.type === 'figure') {
      if (figureLoading) {
        return '*Loading figure...*';
      }
      if (figureUrl) {
        const caption = sourceElement.caption ? `\n\n*${sourceElement.caption}*` : '';
        return `![Figure](${figureUrl})${caption}`;
      }
      return sourceElement.caption || sourceElement.content || '*Figure*';
    }

    if (sourceElement.type === 'table' && sourceElement.caption) {
      return `*${sourceElement.caption}*\n\n${sourceElement.content}`;
    }

    return sourceElement.content;
  };

  const pageInfo = sourceElement.page_index > 0 ? `Page ${sourceElement.page_index}` : undefined;

  return (
    <SourcePopover content={getDisplayContent()} pageInfo={pageInfo} clickable={clickable} onOpen={loadFigure}>
      {children}
    </SourcePopover>
  );
}
